import { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3 } from "lucide-react";
import MetricsHeader, { type HeaderState } from "@/components/metrics/MetricsHeader";
import ControlCenterTab from "@/components/metrics/ControlCenterTab";
import ExploreTab from "@/components/metrics/ExploreTab";
import MovementsTab from "@/components/metrics/MovementsTab";
import CustomersTab from "@/components/metrics/CustomersTab";
import CustomerProfileSheet from "@/components/metrics/CustomerProfileSheet";
import TrialsTab from "@/components/metrics/TrialsTab";
import CancellationsTab from "@/components/metrics/CancellationsTab";
import ForecastTab from "@/components/metrics/ForecastTab";
import RecoverTab from "@/components/metrics/RecoverTab";
import GoalsTab from "@/components/metrics/GoalsTab";
import MetricsSettingsTab from "@/components/metrics/MetricsSettingsTab";
import { resolvePreset, type Granularity, type RangePreset, type ScopedParams, type Segment } from "@/lib/metrics";

const TABS = [
  { id: "control", label: "Control Center" },
  { id: "explore", label: "Explore" },
  { id: "movements", label: "MRR movements" },
  { id: "customers", label: "Customers" },
  { id: "trials", label: "Trials" },
  { id: "cancellations", label: "Cancellations" },
  { id: "forecast", label: "Forecast" },
  { id: "recover", label: "Recover" },
  { id: "goals", label: "Goals & notes" },
  { id: "settings", label: "Settings" },
];

const TAB_IDS = TABS.map((t) => t.id);

function readParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    const tab = p.get("tab") || "control";
    return {
      tab: TAB_IDS.includes(tab) ? tab : "control",
      metric: p.get("metric") || "mrr",
      customer: p.get("customer"),
      user: p.get("user"),
    };
  } catch {
    return { tab: "control", metric: "mrr", customer: null, user: null };
  }
}

/** Keeps the address bar in step so a tab, metric or customer can be linked to. */
function writeParams(next: Record<string, string | null>) {
  try {
    const url = new URL(window.location.href);
    for (const [key, value] of Object.entries(next)) {
      if (value == null || value === "") url.searchParams.delete(key);
      else url.searchParams.set(key, value);
    }
    window.history.replaceState({}, "", `${url.pathname}${url.search}`);
  } catch {
    /* history is not available, the page still works */
  }
}

export default function AdminMetrics() {
  const initial = useMemo(readParams, []);
  const [tab, setTab] = useState(initial.tab);
  const [metricId, setMetricId] = useState(initial.metric);
  const [granularity, setGranularity] = useState<Granularity | "auto">("auto");
  const [customerId, setCustomerId] = useState<string | null>(initial.customer);
  const [pendingUserId, setPendingUserId] = useState<string | null>(initial.user);
  const [movementMonths, setMovementMonths] = useState(12);

  const [header, setHeader] = useState<HeaderState>(() => {
    const preset: RangePreset = "30d";
    return { preset, ...resolvePreset(preset), compare: true, segment: {} as Segment };
  });

  useEffect(() => {
    writeParams({
      tab: tab === "control" ? null : tab,
      metric: tab === "explore" ? metricId : null,
      customer: customerId,
      user: pendingUserId,
    });
  }, [tab, metricId, customerId, pendingUserId]);

  const range = { from: header.from, to: header.to };
  const params: ScopedParams = { ...range, ...header.segment };

  const openMetric = useCallback((id: string) => { setMetricId(id); setTab("explore"); }, []);
  const openCustomer = useCallback((id: string) => { setCustomerId(id); setTab("customers"); }, []);
  const resolvedFromUser = useCallback((id: string) => {
    setCustomerId(id);
    setPendingUserId(null);
  }, []);

  return (
    <div className="space-y-6" data-testid="page-admin-metrics">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <BarChart3 className="w-7 h-7 text-primary" />Metrics
        </h1>
        <p className="text-muted-foreground text-sm">Subscription analytics from a mirror of Stripe. Jamaican dollars, Jamaica time.</p>
      </div>

      <MetricsHeader state={header} onChange={setHeader} />

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto justify-start gap-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`}>{t.label}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="control" className="space-y-6 mt-0">
          <ControlCenterTab params={params} compare={header.compare} onOpenMetric={openMetric} />
        </TabsContent>

        <TabsContent value="explore" className="space-y-6 mt-0">
          <ExploreTab
            params={params}
            compare={header.compare}
            metricId={metricId}
            onSelectMetric={setMetricId}
            granularity={granularity}
            onSelectGranularity={setGranularity}
            onOpenCustomer={openCustomer}
          />
        </TabsContent>

        <TabsContent value="movements" className="space-y-6 mt-0">
          <MovementsTab segment={header.segment} months={movementMonths} onChangeMonths={setMovementMonths} />
        </TabsContent>

        <TabsContent value="customers" className="space-y-6 mt-0">
          <CustomersTab resolveUserId={pendingUserId} onOpenCustomer={resolvedFromUser} />
        </TabsContent>

        <TabsContent value="trials" className="space-y-6 mt-0">
          <TrialsTab onOpenCustomer={openCustomer} />
        </TabsContent>

        <TabsContent value="cancellations" className="space-y-6 mt-0">
          <CancellationsTab range={range} />
        </TabsContent>

        <TabsContent value="forecast" className="space-y-6 mt-0">
          <ForecastTab segment={header.segment} />
        </TabsContent>

        <TabsContent value="recover" className="space-y-6 mt-0">
          <RecoverTab onOpenCustomer={openCustomer} />
        </TabsContent>

        <TabsContent value="goals" className="space-y-6 mt-0">
          <GoalsTab range={range} />
        </TabsContent>

        <TabsContent value="settings" className="space-y-6 mt-0">
          <MetricsSettingsTab />
        </TabsContent>
      </Tabs>

      <CustomerProfileSheet customerId={customerId} onClose={() => setCustomerId(null)} />
    </div>
  );
}
